import {ChangeDetectionStrategy, Component, effect, inject, input, viewChild} from '@angular/core';
import {UIChart} from 'primeng/chart';
import {Chart} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {EvaluationSubCriteriaImportanceScore} from '@frontend/shared-api';
import {TranslocoService} from '@jsverse/transloco';
import {
  IMPORTANCE_SCORE_TYPE_CONFIG,
  SCORED_IMPORTANCE_SCORE_TYPES,
  SummaryChartData,
  SummaryChartDataset,
} from './evaluation-criteria-summary-chart.types';

Chart.register(ChartDataLabels);

@Component({
  selector: 'app-evaluation-criteria-summary-chart',
  imports: [UIChart],
  templateUrl: './evaluation-criteria-summary-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EvaluationCriteriaSummaryChartComponent {
  readonly importanceScores = input.required<EvaluationSubCriteriaImportanceScore[]>();

  private readonly transloco = inject(TranslocoService);

  private readonly chart = viewChild.required(UIChart);

  // chart.js needs one category entry for the single stacked bar to be placed on the hidden axis
  protected readonly chartData: SummaryChartData = {labels: [''], datasets: []};

  protected readonly chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    animation: {
      duration: 300,
      easing: 'linear',
    },
    scales: {
      x: {
        stacked: true,
        display: false,
        min: 0,
        max: 101,
      },
      y: {
        stacked: true,
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
        callbacks: {
          title: (): string => '',
          label: (context: {dataset: {label: string}; raw: number}): string =>
            `${this.transloco.translate(context.dataset.label)}: ${context.raw.toFixed(2)}%`,
        },
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        font: {
          weight: 'bold',
          size: 14,
        },
        formatter: (value: number): string => `${value.toFixed()}%`,
        anchor: 'center',
        align: 'center',
      },
    },
  };

  constructor() {
    effect(() => {
      this.chartData.datasets = this.buildDatasets(this.importanceScores());
      this.chart().refresh();
    });
  }

  private buildDatasets(scores: EvaluationSubCriteriaImportanceScore[]): SummaryChartDataset[] {
    const scoredAttributes = scores.filter(
      (score) => score !== EvaluationSubCriteriaImportanceScore.NOT_RELEVANT,
    );

    return SCORED_IMPORTANCE_SCORE_TYPES.map((type) => {
      const matching = scoredAttributes.filter((score) => score === type).length;
      const percentage =
        scoredAttributes.length > 0 ? (matching / scoredAttributes.length) * 100 : 0;

      const importanceScoreBar: SummaryChartDataset = {
        label: IMPORTANCE_SCORE_TYPE_CONFIG[type].translationKey,
        data: [percentage],
        backgroundColor: IMPORTANCE_SCORE_TYPE_CONFIG[type].color,
        hoverBackgroundColor: IMPORTANCE_SCORE_TYPE_CONFIG[type].color,
        borderWidth: 2,
        borderColor: '#ffffff',
        hoverBorderColor: '#ffffff',
        borderSkipped: false,
        percentage,
      };

      return importanceScoreBar;
    }).filter((importanceScoreBar) => importanceScoreBar.percentage > 0);
  }
}
